import { useContext } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { apiGet, apiPost, apiPatch, apiDelete } from 'api';
import AuthContext from 'context/auth-context';

const getAnalyticsData = async (token) => {
  const url = 'admin/analytics_data';
  const data = await apiGet({ url, token });
  return data;
};

const getAnalyticData = async (token, id) => {
  const url = `admin/analytics_data/${id}`;
  const data = await apiGet({ url, token });
  return data;
};

const createAnalyticData = async (token, params) => {
  const url = 'admin/analytics_data';
  const data = await apiPost({ url, token, objectName: 'analytic', params });
  return data;
};

const updateAnalyticData = async (token, id, params) => {
  const url = `admin/analytics_data/${id}`;
  const data = await apiPatch({ url, token, objectName: 'analytic', params });
  return data;
};

const deleteAnalyticData = async (token, id) => {
  const url = `admin/analytics_data/${id}`;
  const data = await apiDelete({ url, token });
  return data;
};

export function useAdminAnalyticsData({ options } = {}) {
  const token = useContext(AuthContext);
  const queryFn = () => getAnalyticsData(token);
  const queryKey = 'admin-analytics-data';
  return useQuery(queryKey, queryFn, { ...options });
}

export function useAdminAnalyticData({ id, options } = {}) {
  const token = useContext(AuthContext);
  const queryFn = () => getAnalyticData(token, id);
  const queryKey = ['admin-analytic-data', id];
  return useQuery(queryKey, queryFn, { enabled: !!id, ...options });
}

export function useCreateAdminAnalyticData({ options } = {}) {
  const token = useContext(AuthContext);
  const queryClient = useQueryClient();

  return useMutation(
    (params) => createAnalyticData(token, params),
    {
      ...options,
      onSuccess: (data) => {
        queryClient.invalidateQueries('admin-analytics-data');
        if (options?.onSuccess) {
          options.onSuccess(data);
        }
      }
    }
  );
}

export function useUpdateAdminAnalyticData({ id, options } = {}) {
  const token = useContext(AuthContext);
  const queryClient = useQueryClient();

  return useMutation(
    (params) => updateAnalyticData(token, id, params),
    {
      ...options,
      onSuccess: (data) => {
        queryClient.invalidateQueries('admin-analytics-data');
        queryClient.invalidateQueries(['admin-analytic-data', id]);
        if (options?.onSuccess) {
          options.onSuccess(data);
        }
      }
    }
  );
}

export function useDeleteAdminAnalyticData({ options } = {}) {
  const token = useContext(AuthContext);
  const queryClient = useQueryClient();

  return useMutation(
    (id) => deleteAnalyticData(token, id),
    {
      ...options,
      onSuccess: (data) => {
        queryClient.invalidateQueries('admin-analytics-data');
        if (options?.onSuccess) {
          options.onSuccess(data);
        }
      }
    }
  );
}
